/**
 * What a chained step left for the attorney. The server keeps a chained
 * step from asking mid-run (`workflow_turn.py`'s CHAINED_NOTE); Casey ends
 * the reply with an "Open questions:" list instead, which the card shows as
 * Needs You.
 */
import type { UIMessage } from "ai";

import type { ModelUsed } from "@/features/agents/types";
import type { AgentUIMessage } from "@/features/agents/useAgentChat";

import { toolNameOf } from "../chat/components/ToolCard";
import { describeTool } from "../chat/components/toolTitles";
import { turnStatusView } from "../chat/components/TurnStatusLine";

const HEADING = /^\s*(?:\*\*|#+\s*)?open questions\s*:?\s*(?:\*\*)?\s*:?\s*$/i;
const ITEM = /^\s*(?:[-*•]|\d+[.)])\s+(.+?)\s*$/;
const MAX_QUESTIONS = 8;

/** The "Open questions:" list at the end of a reply; [] when there is none. */
export const openQuestions = (text: string): string[] => {
  const lines = text.split("\n");
  let at = -1;
  for (let i = lines.length - 1; i >= 0; i -= 1)
    if (HEADING.test(lines[i])) {
      at = i;
      break;
    }
  if (at < 0) return [];
  const out: string[] = [];
  for (const line of lines.slice(at + 1)) {
    if (!line.trim()) {
      if (out.length) break;
      // eslint-disable-next-line no-continue
      continue;
    }
    const item = ITEM.exec(line);
    if (!item) break;
    const q = item[1].replace(/\*\*/g, "").trim();
    if (q && !/^none\.?$/i.test(q)) out.push(q);
  }
  return out.slice(0, MAX_QUESTIONS);
};

type Part = {
  type: string;
  text?: string;
  data?: { message?: string; label?: string };
};

/** What a step's turn left: failure, the model it ran on, open questions. */
export type TurnReading = {
  failed: string | null;
  stopped: boolean;
  model: string | null;
  questions: string[];
};

/** Read the assistant replies after the step's prompt (`sentId`). */
export const readTurn = (
  messages: UIMessage[],
  sentId: string
): TurnReading => {
  const at = messages.findIndex((m) => m.id === sentId);
  const replies = (at >= 0 ? messages.slice(at + 1) : []).filter(
    (m) => m.role === "assistant"
  );
  const parts = replies.flatMap((m) => m.parts as Part[]);
  const last = replies[replies.length - 1];
  const model =
    [...replies]
      .reverse()
      .map(
        (m) => (m.metadata as { model?: ModelUsed | null } | undefined)?.model
      )
      .find((m) => m?.label)?.label ??
    [...parts].reverse().find((p) => p.type === "data-model" && p.data?.label)
      ?.data?.label ??
    null;
  const text = ((last?.parts ?? []) as Part[])
    .filter((p) => p.type === "text")
    .map((p) => p.text ?? "")
    .join("\n");
  const failure = parts.find((p) => p.type === "data-failed");
  return {
    failed: failure ? failure.data?.message ?? "The turn failed" : null,
    stopped: parts.some((p) => p.type === "data-stopped"),
    model,
    questions: openQuestions(text)
  };
};

/**
 * What the step's turn is doing now, as the chat's status line says it: the
 * running tool call's title ("Researching USCIS Policy Guidance"), a run's
 * status, else "Thinking". Null before Casey's reply starts.
 */
export const liveDetail = (
  messages: UIMessage[],
  sentId: string
): string | null => {
  const at = messages.findIndex((m) => m.id === sentId);
  if (at < 0) return null;
  const reply = [...messages.slice(at + 1)]
    .reverse()
    .find((m) => m.role === "assistant") as AgentUIMessage | undefined;
  if (!reply) return null;
  const now = turnStatusView(reply);
  if (now.kind === "tool")
    return describeTool(toolNameOf(now.part), now.part.input).present;
  return now.label;
};
